import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  Select,
  Spinner,
  Toggle,
  useToast,
} from "@toss-ads/ui";
import {
  createExternalSource,
  deleteExternalSource,
  listAdvertisers,
  listExternalSources,
  updateExternalSource,
} from "../../lib/api";
import type { ExternalSource } from "../../lib/api";

export function SecretsSourcesListPage() {
  const [sourceOpen, setSourceOpen] = React.useState(false);
  const [issuedKey, setIssuedKey] = React.useState<string | null>(null);
  const [editingSource, setEditingSource] =
    React.useState<ExternalSource | null>(null);

  const sources = useQuery({
    queryKey: ["admin", "external-sources"],
    queryFn: listExternalSources,
  });

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h1 className="text-[18px] font-semibold tracking-tight">소스 / 키</h1>
            <p className="text-[12px] text-ink-subtle mt-0.5">
              광고주(수집 버킷)에 표시명을 정하고 API 키 발급. 표시명 비우면
              유입경로(매체)로 표시.
            </p>
          </div>
          <Button size="md" onClick={() => setSourceOpen(true)}>
            + 새 소스
          </Button>
        </div>
        <div className="rounded-lg border bg-surface overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead className="bg-surface-muted">
              <tr>
                <Th width={160}>광고주</Th>
                <Th>표시</Th>
                <Th width={80}>상태</Th>
                <Th width={140}>마지막 리드</Th>
                <Th width={110}>생성일</Th>
                <Th width={72}>수정</Th>
              </tr>
            </thead>
            <tbody>
              {sources.isLoading && (
                <tr>
                  <td colSpan={6} className="py-10">
                    <div className="flex justify-center text-ink-subtle">
                      <Spinner size={18} />
                    </div>
                  </td>
                </tr>
              )}
              {!sources.isLoading && (sources.data ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-10 text-center text-[13px] text-ink-subtle"
                  >
                    등록된 소스가 없습니다.
                  </td>
                </tr>
              )}
              {(sources.data ?? []).map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <Td className="text-ink">{s.advertiserName}</Td>
                  <Td>
                    {s.label ? (
                      <span className="text-ink">{s.label}</span>
                    ) : (
                      <Badge variant="neutral">유입경로</Badge>
                    )}
                  </Td>
                  <Td>
                    <Badge variant={s.active ? "success" : "neutral"} dot>
                      {s.active ? "활성" : "비활성"}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">
                    {s.lastLeadAt ? (
                      formatDateTime(s.lastLeadAt)
                    ) : (
                      <span className="text-ink-subtle">—</span>
                    )}
                  </Td>
                  <Td className="text-ink-muted">{formatDate(s.createdAt)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingSource(s)}
                    >
                      수정
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <CreateSourceModal
        open={sourceOpen}
        onClose={() => setSourceOpen(false)}
        onIssued={(key) => setIssuedKey(key)}
      />
      <KeyRevealModal apiKey={issuedKey} onClose={() => setIssuedKey(null)} />
      <EditSourceModal
        source={editingSource}
        onClose={() => setEditingSource(null)}
      />
    </div>
  );
}

// 소스 수정 — 표시명(label) · 활성 여부.
function EditSourceModal({
  source,
  onClose,
}: {
  source: ExternalSource | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [label, setLabel] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [confirmDel, setConfirmDel] = React.useState(false);

  React.useEffect(() => {
    if (source) {
      setLabel(source.label ?? "");
      setActive(source.active);
      setConfirmDel(false);
    }
  }, [source]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "external-sources"] });
    qc.invalidateQueries({ queryKey: ["leads", "sources"] });
  };

  const del = useMutation({
    mutationFn: () => deleteExternalSource(source!.id),
    onSuccess: () => {
      invalidate();
      toast.success("삭제되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "삭제 실패"),
  });

  const save = useMutation({
    mutationFn: () => {
      if (!source) throw new Error("no source");
      return updateExternalSource(source.id, {
        active,
        label: label.trim() || null,
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success("저장되었습니다");
      onClose();
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "저장 실패"),
  });

  return (
    <Modal
      open={!!source}
      onClose={onClose}
      title="소스 수정"
      description={source ? source.advertiserName : undefined}
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="edit-source-label">표시명 (label)</Label>
          <Input
            id="edit-source-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="비우면 유입경로(매체)로 표시"
            autoComplete="off"
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            리드 유입에 이 값 그대로 표기. 비우면 매체(유입경로)로 표시.
          </p>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div>
            <div className="text-[13px] text-ink">활성</div>
            <div className="text-[11px] text-ink-subtle">
              끄면 이 키로 오는 리드 수신이 막힙니다.
            </div>
          </div>
          <Toggle checked={active} onCheckedChange={setActive} />
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
        {confirmDel ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-danger">삭제?</span>
            <Button
              variant="danger"
              size="md"
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              삭제 확정
            </Button>
            <Button
              variant="text"
              size="md"
              onClick={() => setConfirmDel(false)}
            >
              취소
            </Button>
          </div>
        ) : (
          <Button variant="text" size="md" onClick={() => setConfirmDel(true)}>
            <span className="text-danger">삭제</span>
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="text" size="md" onClick={onClose}>
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// 새 소스 — 광고주 + 표시명(직접입력) + 키 발급. 어드민(라벨)은 서버가 자동 생성.
function CreateSourceModal({
  open,
  onClose,
  onIssued,
}: {
  open: boolean;
  onClose: () => void;
  onIssued: (key: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [advertiserId, setAdvertiserId] = React.useState("");
  const [label, setLabel] = React.useState("");

  const advertisers = useQuery({
    queryKey: ["admin", "advertisers"],
    queryFn: listAdvertisers,
    enabled: open,
  });

  React.useEffect(() => {
    if (open) {
      setAdvertiserId("");
      setLabel("");
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      createExternalSource({ advertiserId, label: label.trim() || null }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "external-sources"] });
      qc.invalidateQueries({ queryKey: ["leads", "sources"] });
      onClose();
      if (res.apiKey) onIssued(res.apiKey);
      toast.success("소스가 생성되었습니다");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "생성 실패"),
  });

  const valid = !!advertiserId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 소스 + 키 발급"
      description="광고주(수집 버킷)에 표시명을 정하고 API 키를 발급합니다."
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="new-source-adv">광고주(수집 버킷)</Label>
          <Select
            id="new-source-adv"
            value={advertiserId}
            onChange={(e) => setAdvertiserId(e.target.value)}
          >
            <option value="">선택…</option>
            {(advertisers.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.slug} — {a.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="new-source-label">표시명 (label)</Label>
          <Input
            id="new-source-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="비우면 유입경로(매체)로 표시"
            autoComplete="off"
          />
          <p className="text-[11px] text-ink-subtle mt-1">
            리드 유입에 이 값 그대로 표기 (예: 스카이벤처스[휘담] / APA). 비우면 매체.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button variant="text" size="md" onClick={onClose}>
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!valid}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          생성 + 키 발급
        </Button>
      </div>
    </Modal>
  );
}

function KeyRevealModal({
  apiKey,
  onClose,
}: {
  apiKey: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  return (
    <Modal
      open={!!apiKey}
      onClose={onClose}
      title="API 키 발급됨"
      description="이 키는 지금만 표시됩니다. 복사해서 벤더에 전달하세요 (이후 복구 불가)."
    >
      <div className="rounded-base border border-border bg-surface-muted p-3 font-mono text-[13px] break-all select-all">
        {apiKey}
      </div>
      <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
        <Button
          variant="outline"
          size="md"
          onClick={() => {
            if (apiKey) {
              void navigator.clipboard.writeText(apiKey);
              toast.success("복사됨");
            }
          }}
        >
          복사
        </Button>
        <Button variant="primary" size="md" onClick={onClose}>
          확인 (닫으면 다시 못 봄)
        </Button>
      </div>
    </Modal>
  );
}

function Th({
  children,
  width,
}: {
  children: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      style={{ width }}
      className="px-2.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-subtle border-b border-border whitespace-nowrap"
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={`px-2.5 py-3 align-middle ${className ?? ""}`}>{children}</td>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}.${day} ${hh}:${mm}`;
}
